import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

interface TimeData {
  from: {
    date: string | null;
    time: string | null;
  };
  to: {
    date: string | null;
    time: string | null;
  };
}

interface Timezone {
  code: string;
  name: string;
}

export const timeAgo = (createdAt: string): string => {
  if (!createdAt) return "";
  try {
    const date = dayjs(createdAt);
    if (!date.isValid()) {
      return "Invalid date";
    }
    const now = dayjs();

    const diffInDays = Math.abs(now.diff(date, "day"));

    // Less than 7 days, use "x days ago" or "x minutes ago"
    if (diffInDays < 7) {
      return date.fromNow();
    } else {
      // 7 days or more, use the date format "D MMM YYYY"
      return date.format("D MMM YYYY");
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

export const convertTimeRange = (
  data: TimeData | undefined,
  timeFormat?: string | null
): string | null => {
  if (!data || !timeFormat) return null;

  try {
    const fromISO =
      data.from.date && data.from.time
        ? new Date(`${data.from.date}T${data.from.time}:00Z`)
            .toISOString()
            .replace(/\.\d{3}Z$/, "Z")
        : null;

    const toISO =
      data.to.date && data.to.time
        ? new Date(`${data.to.date}T${data.to.time}:00Z`)
            .toISOString()
            .replace(/\.\d{3}Z$/, "Z")
        : null;

    if (!fromISO && !toISO) return null;

    return `${fromISO ?? null}/${toISO ?? null}&${timeFormat}`;
  } catch (error) {
    console.error(error instanceof Error ? error.message : "Unknown error");
    return null;
  }
};

/**
 * Converts a UTC timestamp string to a specific timezone.
 *
 * @param text - The UTC timestamp string in ISO format (e.g., "2025-03-30T11:30:00Z")
 * @param timezone - The timezone object containing code and name properties
 * @param timezone.code - IANA timezone identifier (e.g., "Asia/Jayapura")
 * @param timezone.name - Human-readable timezone name (e.g., "UTC+09:00 (Eastern Indonesian Standard Time)")
 * @returns The converted timestamp in ISO format with timezone offset, or null if conversion fails
 *
 * @example
 * const utcTime = "2025-03-30T11:30:00Z";
 * const timezone = {
 *   code: "Asia/Jayapura",
 *   name: "UTC+09:00 (Eastern Indonesian Standard Time)"
 * };
 * const result = convertToTimezone(utcTime, timezone);
 * // Returns: "2025-03-30T20:30:00+09:00"
 */
export const convertToTimezone = (
  text: string,
  timezone: Timezone | undefined
): string | null => {
  if (!text || !timezone) return null;

  try {
    // Parse the input text as UTC and convert to the specified timezone
    const utcDate = dayjs.utc(text);

    if (!utcDate.isValid()) {
      console.error("Invalid date format:", text);
      return null;
    }

    // Convert to the specified timezone using the timezone code
    const convertedDate = utcDate.tz(timezone?.code);

    // Return in ISO format with timezone offset
    return convertedDate.format();
  } catch (error) {
    console.error("Error converting to timezone:", error);
    return null;
  }
};

export function getEstimatedTimeFromSeconds(seconds?: number): string {
  if (seconds === undefined || seconds < 0 || Number.isNaN(seconds)) {
    return "Invalid input";
  }

  const totalMinutes = Math.floor(seconds / 60);

  if (totalMinutes <= 60) {
    return `${totalMinutes} min${totalMinutes !== 1 ? "s" : ""}`;
  }

  const hours = Math.round(totalMinutes / 60); // Round to nearest hour
  return `${hours} hour${hours !== 1 ? "s" : ""}`;
}
